import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import constants from "../../../../config/config";
import TRIAL_IMAGE from "../../../../assets/new-images/Front Page Pricing.png";
import individual from "../../../../assets/icons/individual-icon.svg";
import business from "../../../../assets/icons/business-icon.svg";
import goverment from "../../../../assets/icons/goverment-icon.svg";
import EventNoteIcon from "@mui/icons-material/EventNote";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import HowToRegIcon from "@mui/icons-material/HowToReg";


const audiences = [
  {
    icon: individual,
    title: "For families",
    body: "Make a plan together. Keep contacts, meeting places, pet details, documents, and property information easier to find when it matters.",
    cta: "Explore family features",
    to: "/ourServices/my-mrf-app",
  },
  {
    icon: business,
    title: "For employers and organizations",
    body: "Offer families a practical preparedness benefit with support for rollout, education, and engagement.",
    cta: "Explore organizational options",
    to: "/business",
  },
  {
    icon: goverment,
    title: "For communities",
    body: "Help households take practical steps toward preparedness through a program that can support local outreach.",
    cta: "Explore Program",
    to: "/government",
  },
];

export const AudienceCards = () => (
  <section className="mrf-section mrf-section--alt" aria-labelledby="audience-heading">
    <div className="mrf-container">
      <h2 id="audience-heading" className="mrf-heading mrf-heading--center">Who MYREDFOLDER® is for</h2>
      <div className="mrf-grid-3" style={{ marginTop: 28 }}>
        {audiences.map((a) => (
          <article key={a.title} className="mrf-card">
            <img className="mrf-card__icon" src={a.icon} alt="" aria-hidden="true" />
            <h3 className="mrf-card__title">{a.title}</h3>
            <p className="mrf-card__body">{a.body}</p>
            <Link className="mrf-link" to={a.to}>
              {a.cta} <span aria-hidden="true">→</span>
            </Link>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// Vimeo's embed speaks postMessage, so we can learn when playback ends without
// adding the player SDK.
const AboutVideo = () => {
  const iframeRef = useRef(null);
  const [ended, setEnded] = useState(false);

  const origin = new URL(constants.ABOUT_VIDEO_URL).origin;
  const send = (method, value) =>
    iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ method, value }), origin);

  // Vimeo's postMessage API names the end-of-playback event "finish".
  const subscribe = () => {
    send("addEventListener", "finish");
    send("addEventListener", "play");
  };

  useEffect(() => {
    const onMessage = (e) => {
      if (e.source !== iframeRef.current?.contentWindow) return;
      let data = e.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (data?.event === "ready") {
        subscribe();
      } else if (data?.event === "finish") {
        setEnded(true);
      } else if (data?.event === "play") {
        setEnded(false);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const replay = () => {
    setEnded(false);
    send("play");
  };

  return (
    <div id="about-video" tabIndex={-1} className="mrf-video mrf-about__video">
      <iframe
        ref={iframeRef}
        src={constants.ABOUT_VIDEO_URL}
        title="About MYREDFOLDER® (video)"
        loading="lazy"
        allow="fullscreen; picture-in-picture"
        allowFullScreen
        onLoad={subscribe}
      />
      {ended && (
        <div className="mrf-video__end">
          <a
            className="mrf-btn mrf-btn--primary"
            href={constants.ABOUT_VIDEO_CTA_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {constants.ABOUT_VIDEO_CTA_LABEL}
          </a>
          <button type="button" className="mrf-video__replay" onClick={replay}>
            Replay video
          </button>
        </div>
      )}
    </div>
  );
};

export const AboutPreview = () => (
  <section className="mrf-section mrf-about" aria-labelledby="about-heading">
    <div className="mrf-about__grid">
      <div className="mrf-about__copy">
        <h2 id="about-heading" className="mrf-about__title">About MYREDFOLDER®</h2>
        <p className="mrf-about__text">
          MYREDFOLDER® was developed to help families turn preparedness into a plan they can use.
          MYREDFOLDER, Inc. offers the platform, while NEPTA administers the program and brings emergency management
          experience to education, outreach, and organizational implementation.
        </p>
        <Link className="mrf-btn mrf-btn--on-dark" to="/aboutUs/our-history">
          Learn about our story
        </Link>
      </div>
      <AboutVideo />
    </div>
  </section>
);

const steps = [
  {
    icon: EventNoteIcon,
    title: "Build your plan.",
    body: "Add household members, emergency contacts, meeting places, and relevant hazards.",
  },
  {
    icon: FolderCopyIcon,
    title: "Organize what matters.",
    body: "Keep important records, pet details, and a home inventory together.",
  },
  {
    icon: HowToRegIcon,
    title: "Review it together.",
    body: "Make sure your household knows the plan and update it as life changes.",
  },
];

// Numbered badges sit on a connecting line (see .mrf-steps in refresh.css) so
// the three steps read as one left-to-right sequence rather than three cards.
export const ThreeSteps = () => (
  <section id="how-it-works" tabIndex={-1} className="mrf-section" aria-labelledby="steps-heading">
    <div className="mrf-container">
      <h2 id="steps-heading" className="mrf-heading mrf-heading--center">Three simple steps</h2>
      <ol className="mrf-steps">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={s.title} className="mrf-steps__item">
              <span className="mrf-steps__badge" aria-hidden="true">{i + 1}</span>
              <div className="mrf-steps__panel">
                <span className="mrf-steps__icon" aria-hidden="true">
                  <Icon />
                </span>
                <h3 className="mrf-steps__title">{s.title}</h3>
                <p className="mrf-steps__body">{s.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  </section>
);

// Replaces the Basic/Premium pricing cards. No prices on the homepage.
export const TrialPromo = () => (
  <section className="mrf-section" aria-label="Start your free trial">
    <div className="mrf-container">
      <Link to="/pricing" style={{ display: "block", maxWidth: 900, margin: "0 auto" }}>
        <img
          src={TRIAL_IMAGE}
          alt="MYREDFOLDER: your family, your plan, ready together. View pricing."
          style={{ display: "block", width: "100%", height: "auto", borderRadius: 16 }}
        />
      </Link>
    </div>
  </section>
);
